import { onBeforeUnmount } from "vue";

/**
 * One-shot confetti burst in the brand palette, drawn on a throwaway
 * full-viewport canvas so it needs no dependency and no DOM hook-up. Fires
 * from the bottom-center of the viewport and fades out over ~2.5s; the
 * canvas removes itself when the last piece settles. Respects
 * prefers-reduced-motion by doing nothing at all.
 */
const COLOURS = [
    "#f21439",
    "#191e3e",
    "#ffb545",
    "#c5e12e",
    "#63009c",
    "#eb3700",
];
const PIECES = 160;
const LIFETIME_MS = 2600;

export function useConfetti() {
    let canvas = null;
    let frame = 0;

    function cleanup() {
        cancelAnimationFrame(frame);
        canvas?.remove();
        canvas = null;
    }

    function burst() {
        if (
            typeof window === "undefined" ||
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        ) {
            return;
        }

        cleanup();

        canvas = document.createElement("canvas");
        canvas.setAttribute("aria-hidden", "true");
        canvas.style.cssText =
            "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:70;";
        document.body.appendChild(canvas);

        const dpr = window.devicePixelRatio || 1;
        const width = window.innerWidth;
        const height = window.innerHeight;
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);

        // Launch speed scales with the viewport so the fountains reach the
        // top on tall screens without overshooting short ones.
        const scale = Math.min(1.4, Math.max(0.8, height / 800));

        const pieces = Array.from({ length: PIECES }, (_, i) => {
            // Two fountains, left and right of centre, aimed up and outward.
            const side = i % 2 ? 1 : -1;
            const angle = -Math.PI / 2 + side * (0.08 + Math.random() * 0.55);
            const speed = (15 + Math.random() * 12) * scale;

            return {
                x: width / 2 + side * (30 + Math.random() * 90),
                y: height + 10,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                w: 6 + Math.random() * 7,
                h: 4 + Math.random() * 7,
                rotation: Math.random() * Math.PI,
                spin: (Math.random() - 0.5) * 0.3,
                drift: (Math.random() - 0.5) * 0.2,
                colour: COLOURS[i % COLOURS.length],
            };
        });

        const start = performance.now();

        function tick(now) {
            const elapsed = now - start;
            const fade = Math.max(0, 1 - elapsed / LIFETIME_MS);

            ctx.clearRect(0, 0, width, height);

            pieces.forEach((p) => {
                p.vy += 0.3; // gravity
                p.vx += p.drift * 0.1;
                p.vx *= 0.99;
                p.x += p.vx;
                p.y += p.vy;
                p.rotation += p.spin;

                ctx.save();
                ctx.globalAlpha = fade;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);
                ctx.fillStyle = p.colour;
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                ctx.restore();
            });

            if (elapsed < LIFETIME_MS) {
                frame = requestAnimationFrame(tick);
            } else {
                cleanup();
            }
        }

        frame = requestAnimationFrame(tick);
    }

    onBeforeUnmount(cleanup);

    return { burst };
}
